import styled from "@emotion/styled";
import { forwardRef } from "react";

import Clickable from "./Clickable.jsx";
import parseStyle from "./parseStyle";
import useThemeProps from "./useThemeProps.jsx";

const ButtonRoot = styled(Clickable, {
  shouldForwardProp: (prop) =>
    ![
      "style",
      "hoverStyle",
      "focusStyle",
      "activeStyle",
      "disabledStyle",
      "activeHoverStyle",
    ].includes(prop),
})`
  ${({ theme }) => theme.styleUtils.buttonReset}

  /* CUSTOMIZATIONS */
  border: 1px solid transparent;
  border-radius: 0.25rem;
  padding: 0.5rem 0.75rem;
  display: inline-flex;
  place-content: center;
  place-items: center;
  gap: 0.25rem;
  font-weight: 600;
  font-size: inherit;
  line-height: 1.25em;

  --icon-color: currentColor;

  &.wsui-is-interactive:not(input:disabled + &.wsui-is-interactive:is(label)) {
    cursor: pointer;
  }

  ${({ theme, style }) => parseStyle(style, theme)}

  &.wsui-is-interactive:hover:not(input:disabled + &.wsui-is-interactive:is(label)),
  &:focus,
  input:focus + &:is(label) {
    ${({ theme, hoverStyle }) => parseStyle(hoverStyle, theme)};
  }

  &:focus-visible,
  input:focus-visible + &:is(label) {
    ${({ theme, focusStyle }) => parseStyle(focusStyle, theme)};
  }

  &.wsui-is-interactive:active:not(
      input:disabled + &.wsui-is-interactive:is(label)
    ),
  &[aria-current],
  input:checked + &.wsui-is-interactive:is(label) {
    ${({ theme, activeStyle }) => parseStyle(activeStyle, theme)};

    &.wsui-is-interactive:hover:not(
        input:disabled + &.wsui-is-interactive:is(label)
      ),
    &:focus,
    input:focus + &:is(label) {
      ${({ theme, activeHoverStyle }) => parseStyle(activeHoverStyle, theme)};
    }
  }

  &.wsui-is-disabled,
  &:disabled,
  input:disabled + &.wsui-is-interactive:is(label) {
    pointer-events: none;
    ${({ theme, disabledStyle }) => parseStyle(disabledStyle, theme)};
  }
`;

export default forwardRef(function Button(props, ref) {
  props = useThemeProps({ props, name: "Button", handleVariant: true });
  // const theme = useTheme();
  let {
    color = "gray",
    hoverColor = "hover",
    activeColor = "active",
    activeHoverColor = "hover",
    disabledColor = "gray",
    style = {},
    hoverStyle = {},
    focusStyle = {},
    activeStyle = {},
    activeHoverStyle = {},
    disabledStyle = {},
    width,
    align,
    ...forwardedProps
  } = props;

  style = {
    backgroundColor: [color],
    color: [color, "text"],
    width,
    textAlign: align,
    justifyContent: align,
    justifyItems: align,
    ...style,
  };
  hoverStyle = {
    backgroundColor: [style.backgroundColor || color, hoverColor],
    color: [style.backgroundColor || color, hoverColor, "text"],
    ...hoverStyle,
  };
  focusStyle = {
    // outlineOffset: "1px",
    // outlineStyle: "solid",
    // outlineWidth: "2px",
    ...focusStyle,
  };
  activeStyle = {
    backgroundColor: [style.backgroundColor || color, activeColor],
    color: [style.backgroundColor || color, activeColor, "text"],
    ...activeStyle,
  };
  activeHoverStyle = {
    backgroundColor: [activeStyle.backgroundColor, activeHoverColor],
    color: [activeStyle.backgroundColor, activeHoverColor, "text"],
    ...activeHoverStyle,
  };
  disabledStyle = {
    backgroundColor: [style.backgroundColor || color, disabledColor],
    color: [style.backgroundColor || color, disabledColor, "text"],
    ...disabledStyle,
  };

  return (
    <ButtonRoot
      ref={ref}
      style={style}
      hoverStyle={hoverStyle}
      focusStyle={focusStyle}
      activeStyle={activeStyle}
      disabledStyle={disabledStyle}
      activeHoverStyle={activeHoverStyle}
      {...forwardedProps}
    />
  );
});
